import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import SearchIcon from "@material-ui/icons/Search";
import PhoneIcon from '@material-ui/icons/Phone';
import CallMadeIcon from '@material-ui/icons/CallMade';
import CallReceivedIcon from '@material-ui/icons/CallReceived';
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import IncomingCallNotification from "../../components/WavoipCall/IncomingCallNotification";

const reducer = (state, action) => {
  if (action.type === "LOAD_CALL_HISTORY") {
    const callHistory = action.payload;
    const newCallHistory = [];
    if (isArray(callHistory)) {
      callHistory.forEach((call) => {
        const callIndex = state.findIndex((c) => c.id === call.id);
        if (callIndex !== -1) {
          state[callIndex] = call;
        } else {
          newCallHistory.push(call);
        }
      });
    }
    return [...state, ...newCallHistory];
  }
  if (action.type === "UPDATE_CALL_HISTORY") {
    const call = action.payload;
    const callIndex = state.findIndex((c) => c.id === call.id);
    if (callIndex !== -1) {
      state[callIndex] = call;
      return [...state];
    } else {
      return [call, ...state];
    }
  }
  if (action.type === "RESET") {
    return [];
  }
};

const CallHistoricals = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [callHistory, dispatch] = useReducer(reducer, []);
  const [statistics, setStatistics] = useState({
    total: 0,
    totalReject: 0,
    totalServed: 0,
    totalFinish: 0
  });
  const { user, socket } = useContext(AuthContext);
  const [wavoipToken, setWavoipToken] = useState(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCallHistory();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onCallHistoryEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CALL_HISTORY", payload: data.record });
      }
    };
    if (socket) {
      socket.on(`company-${companyId}-call-history`, onCallHistoryEvent);
    }
    return () => {
      if (socket) {
        socket.off(`company-${companyId}-call-history`, onCallHistoryEvent);
      }
    };
  }, [socket, user.companyId]);

  useEffect(() => {
    const fetchWavoipToken = async () => {
      try {
        const { data } = await api.get("/call/whatsapp/wavoip");
        setWavoipToken(data?.whatsapp?.wavoip || null);
      } catch (err) {
        console.error("Erro ao buscar token Wavoip:", err);
        setWavoipToken(null);
      }
    };
    fetchWavoipToken();
  }, []);

  const fetchCallHistory = async () => {
    try {
      console.log('🔍 Iniciando busca de histórico...', { searchParam, pageNumber });
      const { data } = await api.get("/call/historical", {
        params: { searchParam, pageNumber },
      });
      console.log('📊 Dados recebidos:', data);
      dispatch({ type: "LOAD_CALL_HISTORY", payload: data.historical.resultFinal });
      setStatistics({
        total: data.historical.total || 0,
        totalReject: data.historical.totalReject || 0,
        totalServed: data.historical.totalServed || 0,
        totalFinish: data.historical.totalFinish || 0
      });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      console.error('❌ Erro ao buscar histórico:', err);
      console.error('Detalhes do erro:', err.response?.data);
      toastError(err);
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleMakeCall = async (call) => {
    if (call.url) {
      window.open(call.url, "wavoip", "toolbar=no,scrollbars=no,resizable=no,top=500,left=500,width=500,height=700");
      return;
    }
    let token = call.token_wavoip || wavoipToken;
    if (!token) {
      try {
        const { data } = await api.get("/call/whatsapp/wavoip");
        token = data?.whatsapp?.wavoip || null;
        if (token) {
          setWavoipToken(token);
        }
      } catch (err) {
        toastError("Erro: Token Wavoip não encontrado. Configure o token no WhatsApp.");
        return;
      }
    }
    if (!token) {
      toastError("Erro: Token Wavoip não encontrado. Configure o token no WhatsApp.");
      return;
    }
    if (!call.phone_to) {
      toastError("Erro: Número de telefone não disponível.");
      return;
    }
    const phone = call.phone_to.replace(/\D/g, "");
    const name = call.name || "Contato";
    const url = `https://app.wavoip.com/call?token=${token}&phone=${phone}&name=${name}&start_if_ready=true&close_after_call=true`;
    window.open(url, "wavoip", "toolbar=no,scrollbars=no,resizable=no,top=500,left=500,width=500,height=700");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    if (phone && phone.length >= 10) {
      const cleaned = phone.replace(/\D/g, '');
      if (cleaned.startsWith('55')) {
        const ddd = cleaned.substring(2, 4);
        const number = cleaned.substring(4);
        if (number.length === 9) {
          return `+55 (${ddd}) ${number.substring(0, 5)}-${number.substring(5)}`;
        } else if (number.length === 8) {
          return `+55 (${ddd}) ${number.substring(0, 4)}-${number.substring(4)}`;
        }
      }
    }
    return phone;
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'ENDED':
        return { label: 'Finalizada', color: '#6b7280', bg: '#f3f4f6' };
      case 'ACTIVE':
        return { label: 'Ativa', color: '#3b82f6', bg: '#dbeafe' };
      case 'RINGING':
        return { label: 'Tocando', color: '#8b5cf6', bg: '#ede9fe' };
      case 'BUSY':
        return { label: 'Ocupado', color: '#ef4444', bg: '#fee2e2' };
      case 'NO_ANSWER':
        return { label: 'Não Atendida', color: '#f59e0b', bg: '#fef3c7' };
      case 'REJECTED':
        return { label: 'Rejeitada', color: '#ef4444', bg: '#fee2e2' };
      default:
        return { label: status || 'N/A', color: '#6b7280', bg: '#f3f4f6' };
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div style={{ padding: "24px", background: "#f5f7fa", minHeight: "100vh", boxSizing: "border-box" }}>
      {/* Header */}
      <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a", margin: "0 0 20px 0" }}>
        📞 Histórico de Ligações
      </h1>

      {/* Statistics Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "20px" }}>
        <div style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", borderRadius: "8px", padding: "16px", color: "white", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)" }}>
          <div style={{ fontSize: "28px", fontWeight: 700 }}>{statistics.total}</div>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>Total de Ligações</div>
        </div>
        <div style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", borderRadius: "8px", padding: "16px", color: "white", boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)" }}>
          <div style={{ fontSize: "28px", fontWeight: 700 }}>{statistics.totalReject}</div>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>Rejeitadas</div>
        </div>
        <div style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", borderRadius: "8px", padding: "16px", color: "white", boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)" }}>
          <div style={{ fontSize: "28px", fontWeight: 700 }}>{statistics.totalServed}</div>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>Atendidas</div>
        </div>
        <div style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", borderRadius: "8px", padding: "16px", color: "white", boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)" }}>
          <div style={{ fontSize: "28px", fontWeight: 700 }}>{statistics.totalFinish}</div>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>Finalizadas</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ background: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e8ecf1", marginBottom: "16px", padding: "12px 16px" }}>
        <div style={{ position: "relative" }}>
          <SearchIcon style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "20px" }} />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchParam}
            onChange={handleSearch}
            style={{ width: "100%", padding: "9px 12px 9px 40px", border: "1px solid #e8ecf1", borderRadius: "6px", fontSize: "13px", fontFamily: "inherit", background: "#fafbfc", boxSizing: "border-box" }}
            onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.1)"; }}
            onBlur={(e) => { e.target.style.borderColor = "#e8ecf1"; e.target.style.boxShadow = "none"; }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        style={{ background: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e8ecf1", overflow: "hidden", height: "calc(100vh - 280px)", overflowY: "auto" }}
        onScroll={handleScroll}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f9fafb", borderBottom: "2px solid #e8ecf1", position: "sticky", top: 0, zIndex: 10 }}>
            <tr>
              {["Data/Hora", "Contato", "Telefone", "Direção", "Status", "Duração", "Gravação", "Ações"].map((h, i) => (
                <th key={i} style={{ padding: "10px 14px", textAlign: i > 1 ? "center" : "left", fontSize: "12px", fontWeight: 600, color: "#666", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {callHistory.map((call) => {
              const statusInfo = getStatusInfo(call.status || call.devices?.status);
              const direction = call.direction || call.devices?.direction;
              const duration = call.duration || call.devices?.duration;
              const createdAt = call.createdAt || call.devices?.created_date;
              const phoneTo = call.phone_to || call.devices?.caller || call.devices?.call_to;
              const name = call.name || call.devices?.caller || call.devices?.call_to || "Desconhecido";
              const recordingUrl = call.callSaveUrl || call.recording_url || call.devices?.recording_url;

              return (
                <tr
                  key={call.id || `${call.devices?.id}-${Math.random()}`}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "10px 14px", fontSize: "13px", color: "#1a1a1a", whiteSpace: "nowrap" }}>{formatDate(createdAt)}</td>
                  <td style={{ padding: "10px 14px", fontSize: "13px", color: "#1a1a1a", fontWeight: 500, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</td>
                  <td style={{ padding: "10px 14px", fontSize: "13px", textAlign: "center" }}>
                    <span style={{ background: "#e3f2fd", color: "#1976d2", padding: "3px 8px", borderRadius: "4px", fontSize: "12px", fontFamily: "monospace" }}>
                      {formatPhone(phoneTo)}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                      {direction === 'OUTCOMING' ? (
                        <><CallMadeIcon style={{ fontSize: "15px", color: "#10b981" }} /><span style={{ fontSize: "12px", color: "#10b981", fontWeight: 600 }}>Saída</span></>
                      ) : (
                        <><CallReceivedIcon style={{ fontSize: "15px", color: "#3b82f6" }} /><span style={{ fontSize: "12px", color: "#3b82f6", fontWeight: 600 }}>Entrada</span></>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600, background: statusInfo.bg, color: statusInfo.color }}>
                      {statusInfo.label}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", fontSize: "13px", textAlign: "center", fontFamily: "monospace" }}>
                    {formatDuration(duration)}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    {recordingUrl ? (
                      <a
                        href={recordingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ background: "#3b82f6", color: "white", padding: "5px 10px", display: "inline-flex", alignItems: "center", gap: "4px", borderRadius: "4px", fontSize: "12px", fontWeight: 600, textDecoration: "none" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#2563eb"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#3b82f6"}
                      >
                        ⬇ Baixar
                      </a>
                    ) : (
                      <span style={{ fontSize: "11px", color: "#ccc" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    <button
                      onClick={() => handleMakeCall(call)}
                      style={{ background: "#10b981", color: "white", border: "none", cursor: "pointer", padding: "5px 10px", display: "inline-flex", alignItems: "center", gap: "4px", borderRadius: "4px", fontSize: "12px", fontWeight: 600 }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#059669"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#10b981"}
                    >
                      <PhoneIcon style={{ fontSize: "14px" }} />
                      Ligar
                    </button>
                  </td>
                </tr>
              );
            })}

            {loading && (
              <tr>
                <td colSpan="8" style={{ padding: "40px", textAlign: "center", color: "#999" }}>
                  Carregando...
                </td>
              </tr>
            )}

            {!loading && callHistory.length === 0 && (
              <tr>
                <td colSpan="8" style={{ padding: "40px", textAlign: "center", color: "#999" }}>
                  Nenhuma ligação encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      {wavoipToken && <IncomingCallNotification token={wavoipToken} />}
    </div>
  );
};

export default CallHistoricals;
